import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function renderPermission(value) {
  if (!value) return <span className="text-muted-foreground">-</span>;
  const v = value.toString().toLowerCase();
  if (v === 'ja' || v === 'yes' || v === 'true' || v === '1') {
    return <span className="text-green-500">✓</span>;
  } else if (v === 'nej' || v === 'no' || v === 'false' || v === '0') {
    return <span className="text-red-500">✗</span>;
  } else if (v.includes('zone') || v.includes('egen')) {
    return <span className="text-yellow-500 text-xs">Egen zone</span>;
  }
  return <span className="text-muted-foreground text-xs">{value}</span>;
}

export { renderPermission };

export default function RoleSettingsPanel({ role, eventId, token, availablePermissions, mainRoles = [], allRoles = [], shifts = [], onUpdate }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [areas, setAreas] = useState([]);
  const [workZones, setWorkZones] = useState([]);
  const [assignedAreaIds, setAssignedAreaIds] = useState(
    role.assigned_area_ids || (role.assigned_area_id ? [role.assigned_area_id] : [])
  );
  const [assignedWorkZoneIds, setAssignedWorkZoneIds] = useState(
    role.assigned_work_zone_ids || (role.assigned_work_zone_id ? [role.assigned_work_zone_id] : [])
  );
  const [mainRoleId, setMainRoleId] = useState(role.main_role_id || '');
  const [shiftIds, setShiftIds] = useState(role.shift_ids || []);
  const [canSendTo, setCanSendTo] = useState(role.can_send_to || []);
  const [granularPermissions, setGranularPermissions] = useState({});
  const [granularNotifications, setGranularNotifications] = useState({});
  const [permissionDefinitions, setPermissionDefinitions] = useState(null);
  const [editName, setEditName] = useState(role.name || '');
  const [editDescription, setEditDescription] = useState(role.description || '');
  
  const roleName = role.name || role['Rolle'] || role['rolle'];

  // Anvend samme rækkefølge som i Stamdata > Teams (gemt i localStorage pr. event)
  const sortedAllRoles = (() => {
    if (!Array.isArray(allRoles)) return [];
    let order = [];
    try { order = JSON.parse(localStorage.getItem(`teamOrder:${eventId}`) || '[]'); } catch {}
    const idxMap = new Map();
    order.forEach((id, i) => idxMap.set(id, i));
    return [...allRoles].sort((a, b) => {
      const ai = idxMap.has(a.id) ? idxMap.get(a.id) : Infinity;
      const bi = idxMap.has(b.id) ? idxMap.get(b.id) : Infinity;
      return ai - bi;
    });
  })();

  const PERMISSION_CATEGORIES = [
    { key: 'Zoner', icon: '📍' },
    { key: 'Ressourceøer', icon: '♻️' },
    { key: 'Ressourcespande', icon: '🗑️' },
    { key: 'Maxicontainere', icon: '📦' },
    { key: 'Boder & Bar', icon: '🍔' },
  ];

  const VALUE_CONFIG = {
    'nej': { label: 'Nej', color: 'bg-red-500/20 text-red-400' },
    'ja': { label: 'Ja', color: 'bg-green-500/20 text-green-400' },
    'kun_eget_omraade': { label: 'Ja', color: 'bg-green-500/20 text-green-400' },
    'alle': { label: 'Ja', color: 'bg-green-500/20 text-green-400' },
  };
  
  const getValueConfig = (value) => VALUE_CONFIG[value] || VALUE_CONFIG['nej'];

  useEffect(() => {
    setAssignedAreaIds(role.assigned_area_ids || (role.assigned_area_id ? [role.assigned_area_id] : []));
    setAssignedWorkZoneIds(role.assigned_work_zone_ids || (role.assigned_work_zone_id ? [role.assigned_work_zone_id] : []));
    setMainRoleId(role.main_role_id || '');
    setShiftIds(role.shift_ids || []);
    setEditName(role.name || '');
    setEditDescription(role.description || '');
    setCanSendTo(role.can_send_to || []);
  }, [role.id, role.assigned_area_id, role.assigned_area_ids, role.assigned_work_zone_id, role.assigned_work_zone_ids, role.main_role_id, role.shift_ids, role.name, role.description, role.can_send_to]);

  useEffect(() => {
    if (!roleName) return;
    
    Promise.all([
      axios.get(`${API}/events/${eventId}/available-roles`),
      axios.get(`${API}/areas?event_id=${eventId}`),
      axios.get(`${API}/work-zones?event_id=${eventId}`),
      axios.get(`${API}/permissions/definitions`),
      axios.get(`${API}/events/${eventId}/role-permissions/${encodeURIComponent(roleName)}`)
    ]).then(([rolesRes, areasRes, zonesRes, defsRes, permsRes]) => {
      const roleData = rolesRes.data.find(r => r.role_name === roleName);
      if (roleData) {
        setSettings({ max_capacity: roleData.max_capacity || 0, current_count: roleData.current_count || 0 });
      } else {
        setSettings({ max_capacity: 0, current_count: 0 });
      }
      setAreas(areasRes.data || []);
      setWorkZones(zonesRes.data || []);
      setPermissionDefinitions(defsRes.data);
      setGranularPermissions(permsRes.data.permissions || {});
      setGranularNotifications(permsRes.data.notifications || {});
    }).catch(() => {
      setSettings({ max_capacity: 0, current_count: 0 });
    }).finally(() => setLoading(false));
  }, [eventId, roleName]);

  const saveGranularPermissions = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/events/${eventId}/role-permissions/${encodeURIComponent(roleName)}`,
        { permissions: granularPermissions, notifications: granularNotifications },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Rettigheder gemt");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Kunne ikke gemme rettigheder");
    } finally {
      setSaving(false);
    }
  };

  const saveCapacitySettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/events/${eventId}/role-capacity/${encodeURIComponent(roleName)}`,
        settings,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Kapacitet gemt");
    } catch (error) {
      toast.error("Kunne ikke gemme kapacitet");
    } finally {
      setSaving(false);
    }
  };

  const saveAssignedArea = async () => {
    if (!role.can_edit) { toast.error("Dette team kan ikke redigeres"); return; }
    setSaving(true);
    try {
      await axios.put(
        `${API}/events/${eventId}/roles/${role.id}`,
        { 
          assigned_area_ids: assignedAreaIds,
          assigned_work_zone_ids: assignedWorkZoneIds,
          // Bagudkompatibilitet: sæt også det gamle felt til første element
          assigned_area_id: assignedAreaIds[0] || null,
          assigned_work_zone_id: assignedWorkZoneIds[0] || null,
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Områder og zoner gemt");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Kunne ikke gemme områder og zoner");
    } finally {
      setSaving(false);
    }
  };

  const saveMainRole = async () => {
    if (!role.can_edit) { toast.error("Denne rolle kan ikke redigeres"); return; }
    setSaving(true);
    try {
      await axios.put(
        `${API}/events/${eventId}/roles/${role.id}`,
        { main_role_id: mainRoleId || null },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Funktion tildelt");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Kunne ikke gemme funktion");
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const activePermissionsCount = Object.values(granularPermissions).filter(v => v !== 'nej').length;
  const activeNotificationsCount = Object.values(granularNotifications).filter(v => v && v !== 'nej').length;

  const groupedPermissions = {};
  if (permissionDefinitions?.permissions) {
    permissionDefinitions.permissions.forEach(perm => {
      if (!groupedPermissions[perm.category]) groupedPermissions[perm.category] = [];
      groupedPermissions[perm.category].push(perm);
    });
  }

  const saveNameAndDescription = async () => {
    if (!role.can_edit) { toast.error("Denne rolle kan ikke redigeres"); return; }
    setSaving(true);
    try {
      await axios.put(
        `${API}/events/${eventId}/roles/${role.id}`,
        { name: editName, description: editDescription },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Rolle opdateret");
      if (onUpdate) onUpdate();
      setExpandedSection(null);
    } catch (error) {
      toast.error("Kunne ikke gemme ændringer");
    } finally {
      setSaving(false);
    }
  };

  const saveShifts = async () => {
    if (!role.can_edit) { toast.error("Dette team kan ikke redigeres"); return; }
    setSaving(true);
    try {
      await axios.put(
        `${API}/events/${eventId}/roles/${role.id}`,
        { shift_ids: shiftIds },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Vagter gemt");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Kunne ikke gemme vagter");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 space-y-2">
      <div className="pt-3 border-t border-border space-y-1">
        {/* Navn og beskrivelse sektion */}
        {role.can_edit && (
          <div>
            <button onClick={() => toggleSection('nameDesc')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full text-left py-1">
              {expandedSection === 'nameDesc' ? '▼' : '▶'} Navn og beskrivelse
            </button>
            {expandedSection === 'nameDesc' && (
              <div className="mt-2 p-3 bg-muted/30 rounded-lg space-y-3">
                <div>
                  <label className="text-sm font-medium block mb-1">Teamnavn</label>
                  <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="w-full px-3 py-2 bg-zinc-800 border border-border rounded text-sm" placeholder="Teamnavn" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Beskrivelse</label>
                  <input type="text" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} className="w-full px-3 py-2 bg-zinc-800 border border-border rounded text-sm" placeholder="Kort beskrivelse af teamet" />
                </div>
                <Button size="sm" onClick={saveNameAndDescription} disabled={saving}>{saving ? 'Gemmer...' : 'Gem ændringer'}</Button>
              </div>
            )}
          </div>
        )}

        {/* Funktioner sektion */}
        {mainRoles.length > 0 && (
          <div>
            <button onClick={() => toggleSection('mainRole')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full text-left py-1">
              {expandedSection === 'mainRole' ? '▼' : '▶'} Funktioner
              {mainRoleId && <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 ml-auto">{mainRoles.find(mr => mr.id === mainRoleId)?.name || 'Tildelt'}</span>}
            </button>
            {expandedSection === 'mainRole' && (
              <div className="mt-2 p-3 bg-muted/30 rounded-lg space-y-3">
                <div>
                  <label className="text-sm font-medium block mb-1">Tilhører funktion</label>
                  <select value={mainRoleId} onChange={(e) => setMainRoleId(e.target.value)} className="w-full px-3 py-2 bg-zinc-800 border border-border rounded text-sm">
                    <option value="">Ingen funktion</option>
                    {mainRoles.map(mr => <option key={mr.id} value={mr.id}>{mr.name} {mr.description ? `(${mr.description})` : ''}</option>)}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">Vælg hvilken funktion dette team tilhører (f.eks. Sweeper, Chauffør)</p>
                </div>
                <Button size="sm" onClick={saveMainRole} disabled={saving || !role.can_edit}>{saving ? 'Gemmer...' : 'Gem funktion'}</Button>
              </div>
            )}
          </div>
        )}
        
        {/* Rettigheder sektion */}
        <div>
          <button onClick={() => toggleSection('permissions')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full text-left py-1">
            {expandedSection === 'permissions' ? '▼' : '▶'} Rediger rettigheder
            {activePermissionsCount > 0 && <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400 ml-auto">{activePermissionsCount} aktive</span>}
          </button>
          {expandedSection === 'permissions' && permissionDefinitions && (
            <div className="mt-2 p-3 bg-muted/30 rounded-lg space-y-4 max-h-[400px] overflow-y-auto">
              <div className="flex gap-2 flex-wrap text-xs pb-2 border-b border-border">
                <span className="text-muted-foreground py-1">Hurtigvalg:</span>
                {permissionDefinitions.permission_values.map(v => (
                  <button key={v.value} onClick={() => { const updates = {}; Object.keys(granularPermissions).forEach(k => { updates[k] = v.value; }); setGranularPermissions(updates); }} className={`px-2 py-1 rounded ${getValueConfig(v.value).color} hover:opacity-80`}>
                    Alle "{v.label}"
                  </button>
                ))}
              </div>
              {PERMISSION_CATEGORIES.map(cat => {
                const categoryPerms = groupedPermissions[cat.key] || [];
                if (categoryPerms.length === 0) return null;
                return (
                  <div key={cat.key} className="space-y-1">
                    <div className="flex items-center gap-2 text-sm font-medium px-2 py-1"><span>{cat.icon}</span><span>{cat.key}</span></div>
                    <div className="rounded border border-border/30">
                      {categoryPerms.map((perm, idx) => (
                        <div key={perm.key} className={`flex items-center gap-4 px-3 py-2 hover:bg-primary/10 transition-colors cursor-pointer ${idx < categoryPerms.length - 1 ? 'border-b border-border/20' : ''}`}>
                          <span className="text-sm whitespace-nowrap" style={{width: '480px'}}>{perm.label}</span>
                          <select value={granularPermissions[perm.key] || 'nej'} onChange={(e) => setGranularPermissions({...granularPermissions, [perm.key]: e.target.value})} className={`text-xs px-2 py-1.5 rounded border border-border bg-zinc-800 w-[140px] cursor-pointer ${getValueConfig(granularPermissions[perm.key] || 'nej').color}`}>
                            {permissionDefinitions.permission_values.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              <Button size="sm" onClick={saveGranularPermissions} disabled={saving} className="w-full mt-3">{saving ? 'Gemmer...' : 'Gem rettigheder'}</Button>
            </div>
          )}
        </div>
        
        {/* Kan sende beskeder til */}
        <div>
          <button onClick={() => toggleSection('messaging')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full text-left py-1">
            {expandedSection === 'messaging' ? '▼' : '▶'} Kan sende beskeder til
            {canSendTo.length > 0 && <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 ml-auto">{canSendTo.length} teams</span>}
          </button>
          {expandedSection === 'messaging' && (
            <div className="mt-2 p-3 bg-muted/30 rounded-lg space-y-3 max-h-[300px] overflow-y-auto">
              <p className="text-xs text-muted-foreground mb-2">Vælg hvilke teams dette team kan sende beskeder til. Alle kan modtage beskeder.</p>
              {sortedAllRoles.filter(r => r.id !== role.id).length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen andre teams at vælge</p>
              ) : (
                <div className="space-y-1">
                  {sortedAllRoles.filter(r => r.id !== role.id).map(r => (
                    <label key={r.id} className="flex items-center gap-3 p-2 rounded hover:bg-muted/50 cursor-pointer">
                      <input type="checkbox" checked={canSendTo.includes(r.id)} onChange={(e) => { if (e.target.checked) setCanSendTo([...canSendTo, r.id]); else setCanSendTo(canSendTo.filter(id => id !== r.id)); }} className="rounded" />
                      <span className="text-sm">{r.name || r['Rolle']}</span>
                    </label>
                  ))}
                </div>
              )}
              <Button size="sm" onClick={async () => { setSaving(true); try { await axios.put(`${API}/events/${eventId}/roles/${role.id}`, { can_send_to: canSendTo }, { headers: { Authorization: `Bearer ${token}` }}); toast.success('Beskedindstillinger gemt'); onUpdate(); } catch (error) { toast.error('Kunne ikke gemme'); } finally { setSaving(false); } }} disabled={saving} className="w-full mt-3">{saving ? 'Gemmer...' : 'Gem beskedindstillinger'}</Button>
            </div>
          )}
        </div>
        
        {/* Kapacitet */}
        <div>
          <button onClick={() => toggleSection('capacity')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full text-left py-1">
            {expandedSection === 'capacity' ? '▼' : '▶'} Kapacitet
            {settings?.max_capacity > 0 && <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 ml-auto">{settings.current_count}/{settings.max_capacity}</span>}
          </button>
          {expandedSection === 'capacity' && settings && (
            <div className="mt-2 p-3 bg-muted/30 rounded-lg">
              <div>
                <label className="text-sm font-medium block mb-1">Max antal frivillige <span className="font-normal text-muted-foreground">pr. vagt</span> (0 = ubegrænset)</label>
                <div className="flex items-center gap-2">
                  <input type="number" min="0" value={settings.max_capacity} onChange={(e) => setSettings({...settings, max_capacity: parseInt(e.target.value) || 0})} className="w-24 px-2 py-1 bg-zinc-800 border border-border rounded text-sm" />
                  {settings.max_capacity > 0 && <span className="text-sm text-muted-foreground">({settings.current_count} tilmeldt totalt)</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Kapaciteten gælder <strong>pr. vagt</strong>. Eksempel: 5 personer på 9 vagter = op til 45 personer kan vælge teamet i alt.</p>
              </div>
              <Button size="sm" onClick={saveCapacitySettings} disabled={saving} className="mt-3">{saving ? 'Gemmer...' : 'Gem kapacitet'}</Button>
            </div>
          )}
        </div>
        
        {/* Notifikationer */}
        <div>
          <button onClick={() => toggleSection('notifications')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full text-left py-1">
            {expandedSection === 'notifications' ? '▼' : '▶'} Notifikationer
            {activeNotificationsCount > 0 && <span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary ml-auto">{activeNotificationsCount} aktive</span>}
          </button>
          {expandedSection === 'notifications' && permissionDefinitions && (
            <div className="mt-2 p-3 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground mb-3">Vælg hvilke hændelser brugere med denne rolle skal notificeres om</p>
              <div className="rounded border border-border/30">
                {permissionDefinitions.notifications.map((notif, idx) => (
                  <div key={notif.key} className={`flex items-center gap-4 px-3 py-2 hover:bg-primary/10 transition-colors cursor-pointer ${idx < permissionDefinitions.notifications.length - 1 ? 'border-b border-border/20' : ''}`}>
                    <span className="text-sm whitespace-nowrap" style={{width: '480px'}}>{notif.label}</span>
                    <select value={granularNotifications[notif.key] || 'nej'} onChange={(e) => setGranularNotifications({...granularNotifications, [notif.key]: e.target.value})} className={`text-xs px-2 py-1.5 rounded border border-border bg-zinc-800 w-[140px] cursor-pointer ${VALUE_CONFIG[granularNotifications[notif.key] || 'nej']?.color || VALUE_CONFIG['nej'].color}`}>
                      {permissionDefinitions.permission_values.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <Button size="sm" onClick={saveGranularPermissions} disabled={saving} className="mt-3">{saving ? 'Gemmer...' : 'Gem notifikationer'}</Button>
            </div>
          )}
        </div>
        
        {/* Område-tildeling */}
        {role.can_edit && (areas.length > 0 || workZones.length > 0) && (
          <div>
            <button onClick={() => toggleSection('area')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full text-left py-1">
              {expandedSection === 'area' ? '▼' : '▶'} Områder & Zoner
              {(assignedAreaIds.length > 0 || assignedWorkZoneIds.length > 0) && (
                <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 ml-auto">
                  {assignedAreaIds.length > 0 ? `${assignedAreaIds.length} område${assignedAreaIds.length > 1 ? 'r' : ''}` : ''}
                  {assignedAreaIds.length > 0 && assignedWorkZoneIds.length > 0 ? ' + ' : ''}
                  {assignedWorkZoneIds.length > 0 ? `${assignedWorkZoneIds.length} zone${assignedWorkZoneIds.length > 1 ? 'r' : ''}` : ''}
                </span>
              )}
            </button>
            {expandedSection === 'area' && (
              <div className="mt-2 p-3 bg-muted/30 rounded-lg space-y-4">
                {areas.length > 0 && (
                  <div>
                    <label className="text-sm font-medium block mb-1">Områder</label>
                    <p className="text-xs text-muted-foreground mb-2">Vælg hvilke områder dette team tilhører. Flere kan vælges.</p>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {areas.map(area => (
                        <label key={area.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                          assignedAreaIds.includes(area.id) ? 'bg-indigo-500/20 border border-indigo-500/40' : 'hover:bg-zinc-800'
                        }`}>
                          <input
                            type="checkbox"
                            checked={assignedAreaIds.includes(area.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAssignedAreaIds([...assignedAreaIds, area.id]);
                              } else {
                                setAssignedAreaIds(assignedAreaIds.filter(id => id !== area.id));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{area.name}</span>
                        </label>
                      ))}
                    </div>
                    {assignedAreaIds.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-1">Ingen valgt = alle områder (ingen begrænsning)</p>
                    )}
                  </div>
                )}
                {workZones.length > 0 && (
                  <div>
                    <label className="text-sm font-medium block mb-1">Arbejdszoner</label>
                    <p className="text-xs text-muted-foreground mb-2">Vælg hvilke arbejdszoner dette team er ansvarlig for. Flere kan vælges.</p>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {workZones.map(zone => (
                        <label key={zone.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                          assignedWorkZoneIds.includes(zone.id) ? 'bg-green-500/20 border border-green-500/40' : 'hover:bg-zinc-800'
                        }`}>
                          <input
                            type="checkbox"
                            checked={assignedWorkZoneIds.includes(zone.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAssignedWorkZoneIds([...assignedWorkZoneIds, zone.id]);
                              } else {
                                setAssignedWorkZoneIds(assignedWorkZoneIds.filter(id => id !== zone.id));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{zone.name} {zone.area_name ? `(${zone.area_name})` : ''}</span>
                        </label>
                      ))}
                    </div>
                    {assignedWorkZoneIds.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-1">Ingen valgt = ingen zone-begrænsning</p>
                    )}
                  </div>
                )}
                <Button size="sm" onClick={saveAssignedArea} disabled={saving}>{saving ? 'Gemmer...' : 'Gem områder og zoner'}</Button>
              </div>
            )}
          </div>
        )}
        
        {/* Vagter for dette team */}
        {role.can_edit && (
          <div>
            <button onClick={() => toggleSection('shifts')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-full text-left py-1">
              {expandedSection === 'shifts' ? '▼' : '▶'} Vagter
              {shiftIds.length > 0 && <span className="text-xs px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-400 ml-auto">{shiftIds.length} valgt</span>}
            </button>
            {expandedSection === 'shifts' && (
              <div className="mt-2 p-3 bg-muted/30 rounded-lg space-y-3">
                {shifts.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Ingen vagter oprettet endnu. Opret vagter under Vagter-fanen først.</p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">Vælg hvilke vagter dette team kan have. Ved frivillig-tildeling vises kun disse vagter.</p>
                    <div className="max-h-56 overflow-y-auto space-y-2">
                      {Object.entries(
                        shifts.reduce((acc, shift) => { const date = shift.date || 'Ingen dato'; if (!acc[date]) acc[date] = []; acc[date].push(shift); return acc; }, {})
                      ).sort(([a], [b]) => a.localeCompare(b)).map(([date, dateShifts]) => (
                        <div key={date} className="mb-2">
                          <p className="text-xs font-medium text-muted-foreground mb-1">
                            {date !== 'Ingen dato' ? new Date(date + 'T00:00:00').toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric', month: 'short' }) : 'Ingen dato'}
                          </p>
                          <div className="space-y-1 pl-2">
                            {dateShifts.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')).map(shift => (
                              <label key={shift.id} className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-muted/50 rounded text-sm">
                                <input type="checkbox" checked={shiftIds.includes(shift.id)} onChange={(e) => { if (e.target.checked) setShiftIds([...shiftIds, shift.id]); else setShiftIds(shiftIds.filter(id => id !== shift.id)); }} className="rounded" />
                                <span>{shift.start_time} - {shift.end_time}{shift.name && <span className="text-muted-foreground ml-1">({shift.name})</span>}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button size="sm" onClick={saveShifts} disabled={saving}>{saving ? 'Gemmer...' : 'Gem vagter'}</Button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
